import _ from 'lodash';
import {useLegacyMenus} from '../../shared/utilities/personPageType';

export const VIDDLER_AUTH_QUERY = `query LegacyApiQuery {
  video {
    id
    endpoint
    token
  }
}`;

export const CONDOLENCES_QUERY_TEMPATE = _.template(`query LegacyApiQuery {
  guestBooks(id: <%=sourceId%>, first: <%=count%>, after: "<%=cursor%>") {
    isFallback
    condolences {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          condolenceId
          likes
          name {
            firstName
            lastName
          }
          message
          city
          state {
            code
            name
          }
          date
          relationship
          photo {
            url
            width
            height
            caption
          }
          iconUrl
          video {
            id
          }
          audioUrl
        }
      }
    }
  }
}`);

export const GALLERY_QUERY_TEMPATE = _.template(`query LegacyApiQuery {
  guestBookGallery(id: <%=sourceId%>, first: <%=count%>, after: "<%=cursor%>") {
    totalCondolencesCount
    condolences {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          condolenceId
          likes
          name {
            firstName
            lastName
          }
          message
          city
          state {
            code
            name
          }
          date
          relationship
          photo {
            url
            width
            height
            caption
          }
          iconUrl
          video {
            id
          }
          audioUrl
        }
      }
    }
  }
}`);

export const LOCATION_SEARCH_TEMPLATE = _.template(`query LegacyApiQuery {
  locationSearch(location: "<%=location%>", state_id: "<%=stateId%>") {
    totalResults
    city {
      id
      name
    }
    state {
      id
      name
      code
    }
  }
}`);

/** Returns reusable navs and GDPR query elements
 * @returns {string} returns GraphQL partial query
 */
export function getNavsAndGDPR() {
  return `header: menu(type:"header") {
      menu
    }
    footer: menu(type:"footer", siteType: "asr") {
      menu
    }
    subfooter: menu(type:"subfooter", siteType: "asr") {
      menu
    }
    globalSettings {
      quantcastGDPRScript
      funeralHomeClientAdGlobalSetting
    }`;
}

/** Returns GraphQL fragment for acquiring affiliate data
 * @param {string} affiliateIdentifier the affiliate id either as a sitename or an affiliateid
 * @returns {string} returns GraphQL partial query
 */
export function getAffiliateQuery(affiliateIdentifier) {
  return affiliateIdentifier
    ? `
    affiliates(sitename:"${affiliateIdentifier}") {
      sourceId
      corporateId
      description
      siteName
      active
      affiliateType
      logoUrl
      location {
        state {
          code
        }
        country {
          code
        }
      }
    }`
    : '';
}

/** Returns the GraphQL query needed for this planning page
 * @returns {string} returns GraphQL query
 */
export function getPlanningQuery() {
  return `query LegacyApiQuery {
    ${getNavsAndGDPR()}
  }`;
}

/** Returns the GraphQL query needed for funeral home search
 * @param   {string} searchBy   postal code or city state search
 * @param   {string} state      state
 * @param   {string} city       city
 * @param   {string} name       funeral home name
 * @param   {string} postalCode postal code
 * @param   {number} radius     radius of search from postalCode
 * @param   {number} count      number to return
 * @param   {string} cursor     node id of last fh returned in previous search
 * @returns {string} returns GraphQL query
 */
export function searchFuneralHomesQuery(
  searchBy,
  state,
  city,
  name,
  postalCode,
  radius,
  count,
  cursor
) {
  return `query LegacyApiQuery {
    ${cursor ? '' : getNavsAndGDPR()}
    searchFuneralHomes(seachBy:"${searchBy}", state:"${state}", city:"${city}", name:"${name}", postalCode:"${postalCode}", radius:${radius}, first:${count}, after:"${cursor}" ) {
      sourceId
      name
      address1
      address2
      city
      state
      postalCode
      country
      phone
      email
      websiteName
      logo
      directoryUrl
      premiumSubscriber
      fax
      url
      allowFlorists
      showFTD
      spotlightFlowerUrl
      defaultSpotlightImage
      latitude
      longitude
      }
    }
    meta {
      description
      location {
        city
        state
        postalCode
      }
    }
  }`;
}

/**
 * @param   {number} funeralHomeId funeral home id
 * @param   {object} parameters options used when fetching obitauries
 * @returns {string} returns GraphQL query
 */
export function getObituariesFragment(funeralHomeId, parameters = {}) {
  const affiliateId = parameters.affiliateId || 0;
  const count = parameters.count || 10;
  const offset = parameters.offset || 0;

  return `
  funeralHomeObituaries(id: ${funeralHomeId}, count: ${count}, offset: ${offset}, affiliateId: ${affiliateId}) {
    people {
      personId
      obituaries {
        obituaryId
        obituaryUrl
        fullName
        lifespan
        descriptionHtml
        imageUrl
        publishedType
        publishedStartDate
        publishedCompleteDate
        publisherDisplayName
        ampUrl
      }
    }
  }`;
}

/**
 * @param   {number} funeralHomeId funeral home id
 * @param   {object} parameters options used when fetching obitauries
 * @returns {string} returns GraphQL query ready for a gateway fetch
 */
export function getMoreObituariesQuery(funeralHomeId, parameters) {
  return `query LegacyApiQuery {
${getObituariesFragment(funeralHomeId, parameters)}
  }`;
}

/** Returns the GraphQL query needed for the funeral home page
 * @param   {number} fhIndex funeral home id
 * @param   {number} affiliateId associated affiliate id
 * @returns {string} returns GraphQL query
 *
 */
export function getFhProfileQuery(fhIndex, {affiliateId, obitOffset} = {}) {
  return `query LegacyApiQuery {
    ${getNavsAndGDPR()}
    funeralHomeProfile(id: ${fhIndex}) {
      funeralHomeId
      name
      active
      isPremium
      adData {
        displayOverlayOnMobile
      }
      funeralHomeDateStarted
      funeralHomeLastUpdateDate
      profileData {
        phone
        fax
        email
        websiteUrl
        address1
        address2
        displayPrePlanningForm
        prePlanningFormValue
        description
        spotlightFlowerUrl
        corporateType
        fhco
        sciBrandCode
        customLinkUrl
        customLinkText
        suppressRatingsAndReviews
      }
      location {
        city {
          fullName
          urlName
        }
        state {
          code
          fullName
          urlName
        }
        country {
          code
          cultureCode
        }
        postalCode
        latitude
        longitude
      }
      images {
        funeralHomeImageId
        funeralHomeId
        fileName
        imageType
        displayOrder
        includeInCarousel
        url
      }
      logoImageUrl
      primaryImageUrl
      attributesData {
        category
        name
        value
      }
      flowerSettings {
        cultureCode
        showFlowerLink
        nodeDesktopLinkOverrideUrl
        nodeDesktopPersonLinkOverrideUrl
        nodeMobileLinkOverrideUrl
        nodeMobilePersonLinkOverrideUrl
      }
    }
    upcomingServices(id: ${fhIndex}) {
      people {
       personId
       obituaryId
       fullName
       flowersUrl
       obituaryUrl

       services {
         serviceId
         isPrivate
         serviceDate
         serviceType
         isAtFuneralHome
         location {
           name
           city
           address
           state
          }
        }
      }
    }
    nearbyFuneralHomes(id: ${fhIndex}) {
      funeralHomeId
      isPremium
      url
      name
      imageUrl
      starRating
      city
      state
    }${getAffiliateQuery(affiliateId)}
    ${getObituariesFragment(fhIndex, {affiliateId, offset: obitOffset})}
  }`;
}

/** Returns the GraphQL query needed for this obit/person page
 * @param   {string}    pageType person page type key
 * @param   {number}    personId person id
 * @param   {string}    source   source for obit id
 * @param   {string}    sourceId the source obit id
 * @param   {number}    count    number of condolences to fetch
 * @param   {string}    cursor   offset id of the condolence node
 * @returns {string} returns GraphQL query
 */
export function getPersonQuery(
  pageType,
  personId,
  source,
  sourceId,
  count,
  cursor
) {
  const legacyMenus = useLegacyMenus(pageType);

  const toReturn = `query LegacyApiQuery {${
    legacyMenus
      ? `
  header: menu(type:"header") {
    menu
  }
  footer: menu(type:"footer", siteType: "asr") {
    menu
  }
  subfooter: menu(type:"subfooter", siteType: "asr") {
    menu
  }`
      : ''
  }
  globalSettings {
    quantcastGDPRScript
  }
  person(id: "${personId}", source: "${source}", sourceId: "${sourceId}") {
    id
    personId
    sourceId
    isFallback
    displayText {
      showReadMore
      text
      fullSanitized
    }
    name {
      first
      last
      middle
      nick
      maiden
      prefix
      suffix
      full
      short
    }
    portraitImage {
      attribution
      href
      name
      width
      height
    }
    age
    byPageLink
    fromToYears
    dateOfBirth
    dateOfDeath
    obitSources {
      displayText
      person
      location
    }
    location {
      sourceId
      city
      state
      stateCode
      country
    }
    locationDisplay {
      city
      state
      country
    }
    church {
      sourceId
      name
      address
      city
      state
      stateCode
      postalCode
      country
      county
      phone
      areaCode
      website
      image
    }
    obituaries {
      sourceId
      href
      affiliateId
      funeralHomeId
      dateCreated
      obituaryText
      obituaryType
      gaSitename
      personMaxDateEntered
      showReadMore
    }
    funeralHomes {
      sourceId
      name
      address1
      address2
      city
      state
      postalCode
      country
      phone
      email
      websiteName
      logo
      directoryUrl
      premiumSubscriber
    }
    services {
      personId
      id
      serviceType
      dateTimeDescription
      dateUTC
      startTime
      endTime
      location
      address
      city
      state
      country
      postalCode
      isAtFuneralHome
      isAtChurch
      isTBD
      isPrivate
    }
    affiliates {
      affiliateType
      active
      sourceId
      legacyId
      description
      logo
    }
    meta {
      latestDate
      description
      seoCanonicalUrl
    }
    flowers {
      sourceId
      source
      link
      funeralHome {
        sourceId
        name
        address1
        address2
        city
        state
        postalCode
        country
        phone
        email
        websiteName
        logo
        directoryUrl
      }
      church {
        sourceId
        name
        address
        city
        state
        stateCode
        postalCode
        country
        county
        phone
        areaCode
        website
        image
      }
      eventTagDateRecieved {
        label
        value
      }
      eventTagDateDod {
        label
        value
      }
    }
    customDimensions {
      firstToFile
      obitDaysActive
      daysSinceZero
    }
    personLayout {
      heroCTA
      showReadMore
      showFlowersAndGifts
      fhShowFlowers
      layout {
        left
        right
      }
      showPGBCTA
      isNFGB
    }
    partnerOtherData
    guestBook {
      id
      allowGuestBook
      allowGuestBookEntry
      allowGuestBookPhoto
      allowPersonPagePosting
      allowGuestBookVideo
      guestBookGALabel
      totalCondolencesCount
      totalMediaCount
      isFallback
      guestBookLink {
        href
      }
      sourceId
      condolences (first: ${count}, after: "${cursor}") {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            condolenceId
            likes
            name {
              firstName
              lastName
            }
            message
            city
            state {
              code
              name
            }
            date
            relationship
            photo {
              url
              width
              height
              caption
            }
            iconUrl
            video {
              id
            }
            audioUrl
          }
        }
      }
    }
  }
}`;

  return toReturn;
}



// WEBPACK FOOTER //
// ./src/reactModules/redux/templates/queryTemplates.js